import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBuildingSettings } from '../store/building-settings/actions';
import {
  buildingSettingsLoadingByUuidSelector,
  buildingSettingsByUuidSelector,
} from '../store/building-settings/selectors';
import { BuildingSettingsDto } from '../store/building-settings/types';
import { useParams } from 'react-router-dom';
import { uiMetadata as uiMetadataSelector } from '../store/ui-metadata/selectors';

interface UseBuildingSettingsReturn {
  loading: boolean;
  refetch: VoidFunction;
  settings: BuildingSettingsDto | null;
}

const NATIVE_INTEGRATION_NAME = 'NATIVE';

export const useBuildingSettings = (): UseBuildingSettingsReturn => {
  const dispatch = useDispatch();
  const params = useParams<{ ownerId: string }>();
  const buildingUuid = params.ownerId;

  const settingsSelector = useMemo(() => buildingSettingsByUuidSelector(buildingUuid || ''), [buildingUuid]);
  const loadingSelector = useMemo(() => buildingSettingsLoadingByUuidSelector(buildingUuid || ''), [buildingUuid]);

  const settings = useSelector(settingsSelector);
  const loading = useSelector(loadingSelector);
  const uiMetadata = useSelector(uiMetadataSelector);
  const { integration_name } = uiMetadata || {};

  const fetchData = useCallback(() => {
    if (buildingUuid) {
      dispatch(fetchBuildingSettings.request({ buildingUuid }));
    }
  }, [dispatch, buildingUuid]);

  useEffect(() => {
    if (
      buildingUuid &&
      !settings.settings &&
      !loading &&
      !settings.error &&
      integration_name === NATIVE_INTEGRATION_NAME
    ) {
      fetchData();
    }
  }, [buildingUuid, settings, loading, fetchData, integration_name]);

  return { loading, refetch: fetchData, settings: settings?.settings ?? null };
};
